import React, { useState } from 'react';
import { CalendarDays } from 'lucide-react';
import { es } from 'react-day-picker/locale';

import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { aFechaISO } from '../../lib/calendario';

/**
 * La fecha de nacimiento del paciente, con el calendario del sistema.
 *
 * Reemplaza al `<input type="date">`, que se veía distinto en cada navegador
 * (Chrome dibuja una cosa, Safari otra, y en Firefox el ícono cambia de lugar)
 * y que en el teléfono abre el selector del sistema operativo, que no tiene
 * nada que ver con el resto de la reserva.
 *
 * ---
 *
 * Dos decisiones que valen para un cumpleaños y no para cualquier fecha:
 *
 * `captionLayout="dropdown"` con `startMonth`/`endMonth`. El calendario que
 * viene por defecto navega de a un mes con flechitas, y para llegar a 1985
 * desde hoy eso son casi quinientos clicks: sería peor que el campo nativo que
 * viene a reemplazar, que al menos tiene la rueda del año. Con los desplegables
 * se elige año y mes de una.
 *
 * El rango arranca 120 años atrás y termina hoy. El límite de arriba no es
 * decoración: sin él se puede elegir una fecha futura, que siempre es un error
 * de tipeo y deja el turno a nombre de alguien que todavía no nació. La
 * validación de `handleConfirm` lo vuelve a chequear igual, porque este campo
 * no es el único camino.
 *
 * El valor entra y sale como texto `YYYY-MM-DD`, que es lo que ya guardaba el
 * campo anterior y lo que espera el backend: acá adentro se convierte a `Date`
 * y se vuelve a convertir con `aFechaISO`, nunca con `toISOString()`, que en
 * Argentina adelanta un día toda fecha con hora local desde las 21:00.
 */

const HOY = () => {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  return hoy;
};

/** 'YYYY-MM-DD' -> Date local, sin pasar por UTC. */
function aFecha(texto) {
  if (!texto) return undefined;
  const [anio, mes, dia] = String(texto).split('-').map(Number);
  if (!anio || !mes || !dia) return undefined;
  const fecha = new Date(anio, mes - 1, dia);
  return Number.isNaN(fecha.getTime()) ? undefined : fecha;
}

const FORMATO = new Intl.DateTimeFormat('es-AR', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

export default function CampoFechaDeNacimiento({ id, value, onChange, disabled = false }) {
  const [abierto, setAbierto] = useState(false);

  const hoy = HOY();
  const elegida = aFecha(value);

  const desde = new Date(hoy.getFullYear() - 120, 0, 1);

  return (
    <Popover open={abierto} onOpenChange={setAbierto}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          className="campo-nacimiento"
          disabled={disabled}
          aria-label={
            elegida
              ? `Fecha de nacimiento: ${FORMATO.format(elegida)}. Tocá para cambiarla`
              : 'Elegí tu fecha de nacimiento'
          }
        >
          <span className={elegida ? undefined : 'campo-nacimiento__vacio'}>
            {elegida ? FORMATO.format(elegida) : 'Elegí tu fecha'}
          </span>
          <CalendarDays size={18} aria-hidden="true" />
        </button>
      </PopoverTrigger>

      <PopoverContent className="w-auto p-2" align="start">
        <Calendar
          mode="single"
          selected={elegida}
          onSelect={(fecha) => {
            onChange(fecha ? aFechaISO(fecha) : '');
            // Se cierra al elegir: es un solo dato y quedarse abierto tapa los
            // campos de abajo, que es lo que sigue completando.
            if (fecha) setAbierto(false);
          }}
          locale={es}
          captionLayout="dropdown"
          startMonth={desde}
          endMonth={hoy}
          // Sin esto el mes que se abre es el actual y hay que bajar 30 años a
          // mano incluso teniendo los desplegables.
          defaultMonth={elegida || new Date(hoy.getFullYear() - 30, hoy.getMonth(), 1)}
          disabled={{ after: hoy }}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  );
}
